import React, { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Menu, X } from 'lucide-react'
import { useInstitutional } from '../../contexts/InstitutionalContext'
import { useCommonTranslation } from '../../hooks/usePageTranslation'
import LanguageSelector from '../common/LanguageSelector'
import { trackEvent } from '../../utils/analytics'

const Header = () => {
  const { isMenuOpen, actions } = useInstitutional()
  const { t } = useCommonTranslation()
  const [isScrolled, setIsScrolled] = useState(false)

  React.useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const navLinks = [
    { to: '/', label: t('navigation.home') },
    { to: '/sobre', label: t('navigation.about') },
    { to: '/funcionalidades', label: t('navigation.features') },
    { to: '/planos', label: t('navigation.pricing') },
    { to: '/seguranca', label: t('navigation.security') },
    { to: '/blog', label: t('navigation.blog') },
    { to: '/contato', label: t('navigation.contact') },
  ]

  const location = useLocation()
  const isHome = location.pathname === '/'
  const isTransparent = !isScrolled && !isMenuOpen
  const isDarkHero = isHome && isTransparent

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${!isTransparent ? 'bg-white shadow-soft py-2' : 'bg-transparent py-4'
        }`}
    >
      <div className="container-institutional">
        <div className="flex items-center justify-between h-14 md:h-16 lg:h-20">{/* Logo */}
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2.5">
            <img
              src="/logo-temporiza.png"
              alt="Temporiza"
              className="w-8 h-8 object-contain"
            />
            <span className={`text-xl font-bold tracking-tight transition-colors duration-300 ${isDarkHero ? 'text-white' : 'text-secondary-900'
              }`}>
              Temporiza
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6">
            {navLinks.map((link) => (
              link.highlight ? (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`inline-flex items-center gap-1.5 font-semibold text-sm px-3 py-1 rounded-full border transition-colors duration-200 ${isDarkHero
                    ? 'border-primary-300/40 bg-primary-500/20 text-primary-200 hover:bg-primary-500/30 hover:text-white'
                    : 'border-primary-200 bg-primary-50 text-primary-700 hover:bg-primary-100'
                    }`}
                >
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary-400" />
                  {link.label}
                </Link>
              ) : (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`text-sm font-medium transition-colors duration-200 ${isDarkHero
                    ? 'text-white/90 hover:text-white'
                    : 'text-secondary-700 hover:text-primary-600'
                    }`}
                >
                  {link.label}
                </Link>
              )
            ))}
          </nav>

          {/* Desktop CTA Buttons */}
          <div className="hidden xl:flex items-center space-x-3">
            <LanguageSelector className={`mr-1 ${isDarkHero ? 'text-white' : 'text-secondary-700'}`} />
            <a
              href="https://app.temporiza.com/"
              target="_blank"
              rel="noopener noreferrer"
              className={`btn ${isDarkHero ? 'btn-ghost text-white hover:bg-white/10' : 'btn-ghost'}`}
              onClick={() => trackEvent('cta_click', { location: 'header_login', destination: 'login' })}
            >
              {t('actions.login')}
            </a>
            <a
              href="https://app.temporiza.com/register"
              target="_blank"
              rel="noopener noreferrer"
              className={`btn btn-primary`}
              onClick={() => { trackEvent('cta_click', { location: 'header_signup', destination: 'register' }); trackEvent('signup_initiated', { source: 'header' }) }}
            >
              {t('actions.signup')}
            </a>
          </div>

          {/* Tablet/Mobile Menu Button */}
          <button
            onClick={actions.toggleMenu}
            className={`xl:hidden p-2 transition-colors ${isScrolled || isMenuOpen ? 'text-secondary-700 hover:text-primary-600' : 'text-secondary-700 hover:text-primary-600'
              }`}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black bg-opacity-25 backdrop-blur-sm z-40 lg:hidden"
              onClick={actions.toggleMenu}
              aria-hidden="true"
            />

            {/* Menu Content */}
            <div className="lg:hidden bg-white border-t border-secondary-200 animate-slide-down relative z-50">
              <nav className="py-4 space-y-1">
                {navLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={actions.toggleMenu}
                    className="block px-4 py-3 text-secondary-700 hover:text-primary-600 hover:bg-secondary-50 transition-colors duration-200 min-h-[44px] flex items-center"
                  >
                    {link.label}
                  </Link>
                ))}

                {/* Mobile CTA Buttons */}
                <div className="px-4 pt-4 space-y-3 border-t border-secondary-200 mt-4">
                  <div className="mb-4">
                    <LanguageSelector compact className="w-full" />
                  </div>
                  <a
                    href="https://app.temporiza.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => { actions.toggleMenu(); trackEvent('cta_click', { location: 'header_login', destination: 'login' }) }}
                    className="block w-full btn btn-ghost text-center"
                  >
                    {t('actions.login')}
                  </a>
                  <a
                    href="https://app.temporiza.com/register"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => { actions.toggleMenu(); trackEvent('cta_click', { location: 'header_signup', destination: 'register' }); trackEvent('signup_initiated', { source: 'header' }) }}
                    className="block w-full btn btn-primary text-center"
                  >
                    {t('actions.signup')}
                  </a>
                </div>
              </nav>
            </div>
          </>
        )}
      </div>
    </header>
  )
}

export default Header
